import { clearSessionTokens, getAuthToken } from './session';

/**
 * Why the session ended. Both paths run the same teardown; the reason is
 * carried for participants that want to distinguish an explicit sign-out from
 * a session that died under the member (today none do — it is audit context).
 */
export type AuthTeardownReason = 'sign-out' | 'api-401';

export interface AuthTeardownContext {
  readonly reason: AuthTeardownReason;
  /**
   * Whether an access token was in memory when the session ended — i.e. the
   * tab had actually been signed in, not merely holding a refresh token that
   * never recovered a bearer. Snapshotted before credentials are cleared, so a
   * participant deciding by it sees the state the session died in.
   */
  readonly accessTokenWasLive?: boolean;
}

/**
 * A member-scoped store, cache or hint that must not outlive the session in
 * this tab. Register one participant per owner; `clear` is expected to be
 * idempotent (the same tab can see several session boundaries).
 */
export interface AuthTeardownParticipant {
  /** Stable, unique across the app — duplicate ids are rejected at registration. */
  readonly id: string;
  clear(context: AuthTeardownContext): void | Promise<void>;
}

export interface AuthTeardownOutcome {
  readonly id: string;
  readonly ok: boolean;
}

export interface AuthTeardownResult {
  readonly reason: AuthTeardownReason;
  readonly outcomes: readonly AuthTeardownOutcome[];
}

/**
 * The registry of member-scoped cleanup participants (the web counterpart of
 * the mobile teardown registry, ADR-090 Rules 7–9). Member-scoped state joins
 * teardown by registering here rather than by each end-of-session call site
 * remembering every store: the previous shape left the registration flow
 * store, the onboarding session state and the shared query cache alive across
 * a sign-out, so a second member in the same tab inherited the first member's
 * data.
 */
const participants = new Map<string, AuthTeardownParticipant>();

/**
 * Register a participant. Duplicate ids throw rather than silently replace:
 * two participants under one id means one of them never runs, and that
 * failure is invisible until a member sees another member's data. Returns an
 * unregister function so React effects (StrictMode double-mount) stay balanced.
 */
export function registerAuthTeardownParticipant(participant: AuthTeardownParticipant): () => void {
  if (participants.has(participant.id)) {
    throw new Error(`auth teardown participant "${participant.id}" is already registered`);
  }
  participants.set(participant.id, participant);
  return () => {
    if (participants.get(participant.id) === participant) {
      participants.delete(participant.id);
    }
  };
}

/**
 * Owner self-registration at module load. A store registers itself the moment
 * its module is evaluated — the only way it can come to hold member data — so
 * no shell or screen has to remember it, and a store that was never loaded in
 * this tab has nothing to clear. Idempotent by id: a re-evaluated module (dev
 * HMR, test module reset) replaces its own earlier registration instead of
 * tripping the duplicate guard, which exists for two *different* owners
 * colliding through `registerAuthTeardownParticipant`.
 */
export function ensureAuthTeardownParticipant(participant: AuthTeardownParticipant): void {
  participants.set(participant.id, participant);
}

/** Enumerable so wiring tests can assert nothing member-scoped is missing. */
export function listAuthTeardownParticipants(): readonly AuthTeardownParticipant[] {
  return [...participants.values()];
}

/** Test seam only — production registration happens at module load and app-shell wiring. */
export function clearAuthTeardownParticipants(): void {
  participants.clear();
}

let inFlight: Promise<AuthTeardownResult> | null = null;

/**
 * The one end-of-session routine. Invoked from the explicit sign-out and from
 * the reactive 401 path in `services/api.ts`; no surface may implement its own
 * cleanup path beside it.
 *
 * Credentials are cleared synchronously BEFORE any participant is awaited, so
 * an in-flight request completing mid-teardown cannot re-authenticate or land
 * as the departing member. Participants then run concurrently and isolated —
 * one throwing must not stop the others from clearing, or a single flaky
 * participant would leave far more member data behind than it owns.
 *
 * Single-flight: several bearer-carrying requests failing together after the
 * shared refresh (poll + autosave, say) coalesce onto one run, so participants
 * clear once per session death rather than once per request. A coalesced call
 * does not clear credentials again, so a sign-in that lands inside the few
 * milliseconds an earlier run is still awaiting `cancelQueries` keeps its new
 * tokens; at worst that run's cache clear costs the new member one refetch.
 *
 * The per-participant outcomes are returned for callers and tests; the app
 * deliberately emits no telemetry for a failed participant (no signal is
 * declared for it), since credentials are gone regardless and every other
 * participant still ran.
 */
export function runAuthTeardown(reason: AuthTeardownReason): Promise<AuthTeardownResult> {
  if (inFlight !== null) return inFlight;

  const accessTokenWasLive = getAuthToken() !== null;
  clearSessionTokens();
  const context: AuthTeardownContext = { reason, accessTokenWasLive };
  const run = Promise.all(
    listAuthTeardownParticipants().map(async (participant): Promise<AuthTeardownOutcome> => {
      try {
        await participant.clear(context);
        return { id: participant.id, ok: true };
      } catch {
        return { id: participant.id, ok: false };
      }
    }),
  ).then(
    (outcomes): AuthTeardownResult => {
      inFlight = null;
      return { reason, outcomes };
    },
    (): AuthTeardownResult => {
      // Unreachable in practice (every participant is caught above), kept so a
      // rejected run can never pin `inFlight` and block the next teardown.
      inFlight = null;
      return { reason, outcomes: [] };
    },
  );
  inFlight = run;
  return run;
}
